import { useState, type ReactNode, useEffect, useRef, type RefObject } from "react";
import { createPortal } from "react-dom";
import { BackIcon, CloseIcon } from "./Icons";
import { Link, type LinkProps } from "@tanstack/react-router";
import { common } from "@pagemates/shared/copy";

const txt = common.ui;

/** A face, or initials. `short` gives one initial, for tight overlapping stacks. */
export function Avatar({ name, image, size, short }: { name: string; image?: string | null; size?: "lg"; short?: boolean }) {
  const initials = name.split(/\s+/).slice(0, short ? 1 : 2).map((s) => s[0]?.toUpperCase() ?? "").join("");
  return (
    <span className={`avatar${size === "lg" ? " lg" : ""}`} aria-hidden="true">
      {image ? <img src={image} alt="" /> : initials}
    </span>
  );
}

/**
 * The one back control: "‹ Destination", muted, the first line of the page,
 * with an optional action on the right (a gear, "Add a book"). Every page
 * with a way back uses it, so it sits in the same spot everywhere.
 */
export function BackBar({ label, link, onClick, right }: {
  label: string;
  link?: { to: string; params?: Record<string, string>; search?: Record<string, unknown> };
  onClick?: (e: React.MouseEvent) => void;
  right?: ReactNode;
}) {
  const inner = <><BackIcon width={20} height={20} aria-hidden="true" /><span>{label}</span></>;
  return (
    <div className="backbar">
      {link
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ? <Link {...(link as any)} className="back-link" onClick={onClick}>{inner}</Link>
        : <button type="button" className="back-link" onClick={onClick}>{inner}</button>}
      {right}
    </div>
  );
}

export function TopBar({ title, back, right }: { title: string; back?: LinkProps["to"]; right?: ReactNode }) {
  return (
    <header className="topbar">
      <div className="row grow">
        {back && <Link to={back} search={{ all: true }} className="back" aria-label={txt.back}>←</Link>}
        <span className="title">{title}</span>
      </div>
      {right}
    </header>
  );
}

/**
 * The one empty-state pattern: a dashed card, a serif heading, one line of
 * copy, at most one button and an optional quiet link under it. `art` sits
 * above the heading (only the Reading tab's lamp uses it).
 */
export function EmptyState({ title, children, action, secondary, art, className }:
  { title: string; children?: ReactNode; action?: ReactNode; secondary?: ReactNode; art?: ReactNode; className?: string }) {
  return (
    <div className={`empty-state${className ? ` ${className}` : ""}`}>
      {art}
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action && <div className="es-action">{action}</div>}
      {secondary && <div className="es-secondary">{secondary}</div>}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p className="small">{children}</p>}
    </div>
  );
}

/** Inline, two-step confirmation. The viewer never shows native confirm dialogs. */
export function Confirm({ label, question, onConfirm, busy, className = "danger" }:
  { label: string; question: string; onConfirm: () => void; busy?: boolean; className?: string }) {
  const [open, setOpen] = useState(false);
  if (!open) return <button className={`btn ${className}`} onClick={() => setOpen(true)}>{label}</button>;
  return (
    <div className="confirm">
      <p className="small">{question}</p>
      <div className="row">
        <button className="btn danger sm" disabled={busy} onClick={onConfirm}>{busy ? txt.working : txt.yesDoIt}</button>
        <button className="btn ghost sm" onClick={() => setOpen(false)}>{txt.cancel}</button>
      </div>
    </div>
  );
}

export function RolePill({ role }: { role: string }) {
  if (role === "member") return null;
  return <span className={`pill${role === "owner" ? " accent" : ""}`}>{role}</span>;
}

export function Spinner({ label = txt.loading }: { label?: string }) {
  return <p className="muted small" role="status">{label}…</p>;
}

/** While a sheet or panel is open, the page behind it must not scroll (iOS lets touches chain through the dim). */
export function useScrollLock(open = true) {
  useEffect(() => {
    if (!open) return;
    document.documentElement.classList.add("scroll-locked");
    return () => document.documentElement.classList.remove("scroll-locked");
  }, [open]);
}

/**
 * Pull a phone's bottom sheet down to close it. A drag starts when the
 * touch moves down while whatever it's on is scrolled to the top; past a
 * quarter of the sheet's height, or a quick flick, the sheet slides away
 * and `onClose` runs. Anything less springs back. Off on desktop, where
 * these sheets are panes.
 */
export function useSheetDrag(ref: RefObject<HTMLElement | null>, onClose: () => void) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia("(min-width: 900px)").matches) return;
    let startY = 0, startX = 0, startT = 0, dy = 0, armed = false, dragging = false;
    const scrollerOf = (t: EventTarget | null): HTMLElement | null => {
      for (let n = t as HTMLElement | null; n && n !== el.parentElement; n = n.parentElement) {
        const oy = getComputedStyle(n).overflowY;
        if ((oy === "auto" || oy === "scroll") && n.scrollHeight > n.clientHeight + 1) return n;
        if (n === el) break;
      }
      return null;
    };
    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) { armed = false; return; }
      const t = e.target as HTMLElement;
      if (t.closest("input, textarea, select, [contenteditable]")) { armed = false; return; }
      armed = (scrollerOf(t)?.scrollTop ?? 0) <= 0;
      startY = e.touches[0]!.clientY; startX = e.touches[0]!.clientX; startT = Date.now(); dy = 0; dragging = false;
    };
    const onMove = (e: TouchEvent) => {
      if (!armed) return;
      const d = e.touches[0]!.clientY - startY;
      const dx = e.touches[0]!.clientX - startX;
      if (!dragging) {
        if (d < -4) { armed = false; return; } // scrolling up: leave it to the sheet
        if (Math.abs(dx) > Math.abs(d) && Math.abs(dx) > 6) { armed = false; return; } // sideways: not ours
        // Pulling down at the top: claim the gesture from the very first move. iOS decides on the first
        // touchmove whether the browser scrolls; let that one through and the sheet's contents rubber-band
        // inside a sheet that doesn't move.
        if (d > 0) e.preventDefault();
        if (d < 8) return;
        dragging = true; el.style.animation = "none"; el.style.transition = "none";
      }
      e.preventDefault();
      dy = Math.max(0, d);
      el.style.transform = `translateY(${dy}px)`;
    };
    const onEnd = () => {
      if (!dragging) return;
      dragging = false;
      const flick = dy / Math.max(1, Date.now() - startT) > 0.6;
      if (dy > el.offsetHeight * 0.25 || (flick && dy > 40)) {
        el.style.transition = "transform .18s ease-in"; el.style.transform = "translateY(100%)";
        window.setTimeout(() => close.current(), 170);
      } else {
        el.style.transition = "transform .22s cubic-bezier(.2,.8,.2,1)"; el.style.transform = "";
      }
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart); el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd); el.removeEventListener("touchcancel", onEnd);
    };
  }, [ref]);
}

/** A bottom sheet on phones, a centred dialog on desktop. Escape, the backdrop and a drag down close it. */
export function Sheet({ label, onClose, className, children }: { label: string; onClose: () => void; className?: string; children: React.ReactNode }) {
  useScrollLock();
  const ref = useRef<HTMLDivElement>(null);
  useSheetDrag(ref, onClose);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
  return createPortal(
    <>
      <div className="modal-dim" onClick={onClose} />
      <div className={`modal mo-sheet${className ? ` ${className}` : ""}`} role="dialog" aria-label={label} ref={ref}>
        <div className="grab" />
        <div className="head"><b>{label}</b><button type="button" aria-label={txt.close} onClick={onClose}><CloseIcon /></button></div>
        <div className="mo-body">{children}</div>
      </div>
    </>,
    document.body,
  );
}

